import { Injectable } from "@angular/core";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { SnackComponentComponent } from "../components/snack-component/snack-component.component";

@Injectable({
  providedIn: "root",
})
export class SnackService {
  constructor(private snackBar: MatSnackBar) {}

  dismiss() {
    this.snackBar.dismiss();
  }

  /**
   * Auto-fermeture "intelligente" : au lieu de laisser Material fermer le
   * snack après `duration` (impossible à mettre en pause), on gère un timer
   * manuel + des listeners de survol sur le conteneur :
   *  - survol (mouseenter) → on annule le timer : le snack reste affiché tant
   *    que la souris est dessus (l'utilisateur a le temps de lire / cliquer) ;
   *  - sortie (mouseleave) → on programme la fermeture 3 s plus tard (laisse
   *    le temps de revenir dessus ; un nouveau survol annule cette fermeture).
   * Si la souris ne passe jamais dessus, fermeture normale après `duration`.
   */
  private _autoDismissWithHover(
    ref: MatSnackBarRef<unknown>,
    duration: number,
  ): void {
    const GRACE_AFTER_LEAVE = 3000;
    let timer = setTimeout(() => ref.dismiss(), duration);
    // Le conteneur n'est dans le DOM qu'après le cycle d'ouverture : on
    // attache les listeners au prochain tick.
    setTimeout(() => {
      const containers = document.querySelectorAll(
        ".cdk-overlay-container .mat-mdc-snack-bar-container",
      );
      const el = containers[containers.length - 1] as HTMLElement | undefined;
      if (!el) return;
      el.addEventListener("mouseenter", () => clearTimeout(timer));
      el.addEventListener("mouseleave", () => {
        clearTimeout(timer);
        timer = setTimeout(() => ref.dismiss(), GRACE_AFTER_LEAVE);
      });
    });
  }

  openComponent(c) {
    return this.snackBar.openFromComponent(c, {
      panelClass: ["component-snackbar"],
    });
  }

  success(message: string) {
    const ref = this.snackBar.open(message, "Ok", {
      verticalPosition: "top",
      horizontalPosition: "right",
      panelClass: ["success-snackbar"],
    });
    this._autoDismissWithHover(ref, 5000);
  }

  error(message: string) {
    const ref = this.snackBar.open(message, "Ok", {
      verticalPosition: "top",
      horizontalPosition: "right",
      panelClass: ["error-snackbar"],
    });
    this._autoDismissWithHover(ref, 5000);
  }

  warn(message: string) {
    const ref = this.snackBar.open(message, "Ok", {
      verticalPosition: "top",
      horizontalPosition: "right",
      panelClass: ["warn-snackbar"],
    });
    this._autoDismissWithHover(ref, 5000);
  }

  info(message: string) {
    const ref = this.snackBar.open(message, "Ok", {
      verticalPosition: "top",
      horizontalPosition: "right",
      panelClass: ["info-snackbar"],
    });
    this._autoDismissWithHover(ref, 5000);
  }

  successWithAction(
    messages: string[],
    buttonText: string,
    buttonFunction: () => void,
    buttonIcon?: string,
  ) {
    const snackBarRef = this.snackBar.openFromComponent(
      SnackComponentComponent,
      {
        verticalPosition: "top",
        horizontalPosition: "right",
        panelClass: ["success-snackbar"],
      },
    );
    snackBarRef.instance.messages.set(messages);
    snackBarRef.instance.buttonFunction.set(buttonFunction);
    snackBarRef.instance.buttonText.set(buttonText);
    snackBarRef.instance.buttonIcon.set(buttonIcon);
    this._autoDismissWithHover(snackBarRef, 6000);
  }

  errorWithAction(
    messages: string[],
    buttonText: string,
    buttonFunction: () => void,
    buttonIcon?: string,
  ) {
    const snackBarRef = this.snackBar.openFromComponent(
      SnackComponentComponent,
      {
        verticalPosition: "top",
        horizontalPosition: "right",
        panelClass: ["error-snackbar"],
      },
    );
    snackBarRef.instance.messages.set(messages);
    snackBarRef.instance.buttonFunction.set(buttonFunction);
    snackBarRef.instance.buttonText.set(buttonText);
    snackBarRef.instance.buttonIcon.set(buttonIcon);
    this._autoDismissWithHover(snackBarRef, 8000);
  }

  infoWithAction(
    messages: string[],
    buttonText: string,
    buttonFunction: () => void,
    buttonIcon?: string,
  ) {
    const snackBarRef = this.snackBar.openFromComponent(
      SnackComponentComponent,
      {
        verticalPosition: "top",
        horizontalPosition: "right",
        panelClass: ["info-snackbar"],
      },
    );
    snackBarRef.instance.messages.set(messages);
    snackBarRef.instance.buttonFunction.set(buttonFunction);
    snackBarRef.instance.buttonText.set(buttonText);
    snackBarRef.instance.buttonIcon.set(buttonIcon);
    this._autoDismissWithHover(snackBarRef, 6000);
  }
}
